import type { FirstDayOfWeek, PreferenceValues } from '../contract/types';
import { parseValue } from './instant';
import { dateTimeFormat } from './intl-cache';

/**
 * Calendar arithmetic in the reader's time zone.
 *
 * Separate from the formatters because the answers are values rather than strings: the start of a
 * month is a fact a query needs, not something to show anyone. Features kept writing these by hand
 * — an `isSameDay` here, a `subYears` there — and every one of them silently meant *the host
 * machine's* calendar, which is the wrong one for anybody whose laptop is not where they are.
 *
 * Built on `Intl` and `Date` alone, because R1 leaves no other option. That is less of a constraint
 * than it sounds: `Intl` already knows every zone's offset at every instant, which is the only
 * thing a date library knows that `Date` does not.
 *
 * ## The one hard part
 *
 * Going from an instant to wall-clock fields is easy — `Intl` does it. Going back is not, because
 * the offset to subtract depends on the instant being solved for. The resolution below is the
 * standard two-pass one, and the cases it exists for are real: a day that starts at 1 AM because
 * midnight did not happen (Santiago in September), and an hour that happens twice (any autumn).
 */

/** Both ends of a calendar day, as instants. */
export type DayBounds = {
  /** The first instant of the day. */
  readonly from: Date;
  /** The last instant of the day, a millisecond before the next one begins. */
  readonly to: Date;
};

/**
 * A calendar amount to move by, in the units a calendar counts in.
 *
 * Not milliseconds: a year is not 365 days, and a day across a daylight-saving boundary is not 24
 * hours. Moving a year back from 29 February lands on the 28th, which is the answer a person
 * expects and the one plain arithmetic cannot give.
 */
export type CalendarShift = {
  readonly years?: number;
  readonly months?: number;
  readonly weeks?: number;
  readonly days?: number;
};

export type CalendarBundle = {
  /** The zone every answer here is anchored in, for a caller that has to name it. */
  readonly zone: string;
  /**
   * The day the week starts on here, for a caller configuring something that wants the day itself
   * rather than an answer computed from it.
   *
   * Reading it from the bundle is what keeps it agreeing with {@link startOfWeek}. The reader's
   * profile is not the same answer: like every other convention the bundle reports, this one is
   * en-US while the rollout flag is off, and a caller reaching past the bundle for the raw
   * preference would start the week somewhere the rest of the page does not.
   */
  readonly firstDayOfWeek: FirstDayOfWeek;
  /** Whether two instants fall on the same day here. */
  readonly isSameDay: (a: Date, b: Date) => boolean;
  /** The first instant of the day the value falls on. */
  readonly startOfDay: (value: Date) => Date;
  /** The last instant of that day, a millisecond before the next begins. */
  readonly endOfDay: (value: Date) => Date;
  /** The first instant of the month the value falls on. */
  readonly startOfMonth: (value: Date) => Date;
  /** The last instant of that month. */
  readonly endOfMonth: (value: Date) => Date;
  /**
   * The first instant of the week the value falls on, starting on the day the reader's
   * `firstDayOfWeek` preference names rather than the one their locale would assume.
   */
  readonly startOfWeek: (value: Date) => Date;
  /**
   * The same wall-clock time, a calendar amount away. Negative components move backwards.
   *
   * Overflow clamps to the end of the month, so a month before 31 March is 28 February rather than
   * 2 or 3 March.
   */
  readonly shift: (value: Date, by: CalendarShift) => Date;
  /** The calendar day an instant falls on here, as `2026-08-09`. */
  readonly dayOf: (value: Date) => string;
  /**
   * Both ends of a named day here — what a query means by "the 9th".
   *
   * A day is only a range of instants once somebody says where they are standing. Anchored in UTC
   * instead, a Detroit reader asking for the 9th gets the evening of the 8th and loses their own
   * evening off the end.
   *
   * @param day - A calendar day, as `yyyy-MM-dd`.
   */
  readonly dayBounds: (day: string) => DayBounds;
  /**
   * A named day as the zone-less value `plainDate` and `plainDateLong` render, for showing a day
   * back to the reader rather than querying with it.
   *
   * @param day - A calendar day, as `yyyy-MM-dd`.
   */
  readonly plainDay: (day: string) => Date;
  /**
   * The same value read out of a payload that carries a day as an instant.
   *
   * Plenty of endpoints store a calendar day as midnight UTC rather than as `yyyy-MM-dd`, and a
   * `Date` built straight from one is a moment: render it anywhere west of Greenwich and it names
   * the evening before. What comes back is the day the payload names, ready for `plainDate`, which
   * converts it nowhere.
   *
   * Lenient about the time and strict about the day: an endpoint that pads the day with a working
   * hour, or writes it against its own offset, still means the day it wrote, so the clock is
   * dropped rather than allowed to carry the value into a neighbouring one. A value that is not a
   * date, or that opens on a day no calendar has, has no day to give and comes back as `undefined`
   * rather than as the March that `Date` turns the 31st of February into.
   *
   * Read through the same parser the components use, so the shapes it refuses are refused here too.
   *
   * @param value - A timestamp naming the day meant. A `Date` names none, so its UTC day is taken.
   */
  readonly plainDayFrom: (value: string | Date) => Date | undefined;
};

/** A calendar day, exactly. Anything looser is a shape the caller has not thought about. */
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Reads `yyyy-MM-dd` into fields, refusing anything else.
 *
 * Loud rather than lenient: a day that does not parse has no defensible stand-in, and the quiet
 * ones — today, the epoch — put a wrong range in front of someone who has no way to tell.
 *
 * Matching the shape is not enough to be a day. `2026-02-31` and `2026-13-01` both match, and
 * `Date` answers them with 3 March and January 2027 rather than refusing, which is the quiet wrong
 * answer this is here to prevent. Rebuilding the date and checking every field survived is what
 * separates a day that exists from one that merely looks like it does.
 */
const fieldsOf = (day: string): readonly [number, number, number] => {
  const parts = DAY.exec(day.trim());

  if (!parts) {
    throw new TypeError(`Expected a calendar day as yyyy-MM-dd, got "${day}"`);
  }

  const year = Number(parts[1]);
  const monthIndex = Number(parts[2]) - 1;
  const dayOfMonth = Number(parts[3]);

  // Set rather than constructed, because `Date.UTC` reads a two-digit year as 19xx and would
  // reject `0026-02-01` for being a year it moved itself.
  const rebuilt = new Date(0);
  rebuilt.setUTCFullYear(year, monthIndex, dayOfMonth);

  if (
    rebuilt.getUTCFullYear() !== year ||
    rebuilt.getUTCMonth() !== monthIndex ||
    rebuilt.getUTCDate() !== dayOfMonth
  ) {
    throw new TypeError(`Expected a day that exists, got "${day}"`);
  }

  return [year, monthIndex, dayOfMonth];
};

/** The day a timestamp opens with, which is the only part of it {@link isRealDay} can judge. */
const DAY_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/** Whether a day exists, asked of {@link fieldsOf} so there is one answer to the question. */
const isRealDay = (day: string): boolean => {
  try {
    fieldsOf(day);

    return true;
  } catch {
    return false;
  }
};

/** Wall-clock fields, in the order `Date.UTC` takes them. */
type Wall = readonly [
  year: number,
  monthIndex: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  millisecond: number,
];

/**
 * A fixed locale and the ISO calendar, because these are numbers to compute with rather than to
 * show. A reader whose locale prefers the Buddhist or Japanese calendar would otherwise shift every
 * year number that passes through here.
 */
const WALL_CLOCK_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  calendar: 'iso8601',
};

/**
 * The era on its own, from the Gregorian calendar. ICU 78 reports no era for `iso8601` at all, so
 * the wall clock's 1 BC comes back as a bare year 1; Gregorian still names BC and AD, and agrees
 * with ISO on every day either way.
 */
const ERA_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  calendar: 'gregory',
  era: 'short',
};

/**
 * No zone is more than a day from UTC, so an instant from here on is AD everywhere and the era
 * never needs asking about. Set rather than constructed: `Date.UTC` would read year 1 as 1901.
 */
const CERTAINLY_AD = (() => {
  const boundary = new Date(0);
  boundary.setUTCFullYear(1, 0, 2);
  return boundary.getTime();
})();

const isBeforeChrist = (timeZone: string, instant: number): boolean =>
  instant < CERTAINLY_AD &&
  dateTimeFormat('en-US', { ...ERA_OPTIONS, timeZone })
    .formatToParts(new Date(instant))
    .find((part) => part.type === 'era')?.value === 'BC';

/** The wall-clock reading in `timeZone` at an instant. */
const wallClockIn = (timeZone: string, instant: number): Wall => {
  const parts = dateTimeFormat('en-US', {
    ...WALL_CLOCK_OPTIONS,
    timeZone,
  }).formatToParts(new Date(instant));

  const field = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value);

  // `h23` still reports midnight as 24 in some engines, and a 24 that means the day before would
  // put every `startOfDay` an entire day out.
  const hour = field('hour') % 24;

  // Before year 1 the era flips and the year counts back up, so 1 BC reports as year 1. Negating
  // keeps the arithmetic monotonic. Unreachable from any payload we serve; cheap to be right about.
  const year = isBeforeChrist(timeZone, instant)
    ? 1 - field('year')
    : field('year');

  return [
    year,
    field('month') - 1,
    field('day'),
    hour,
    field('minute'),
    field('second'),
    // `Intl` does not report milliseconds, and the zone offset never carries any, so the instant's
    // own sub-second part is the whole answer.
    ((instant % 1000) + 1000) % 1000,
  ];
};

/**
 * The wall clock read as though it were UTC.
 *
 * `Date.UTC` maps years 0-99 onto the twentieth century, which would quietly rewrite any date
 * before 100 AD; `setUTCFullYear` is the documented way out.
 */
const asUtc = ([
  year,
  monthIndex,
  day,
  hour,
  minute,
  second,
  millisecond,
]: Wall): number => {
  const naive = new Date(0);

  naive.setUTCFullYear(year, monthIndex, day);
  naive.setUTCHours(hour, minute, second, millisecond);

  return naive.getTime();
};

/** How far ahead of UTC `timeZone` is at an instant, in milliseconds. */
const offsetAt = (timeZone: string, instant: number): number =>
  asUtc(wallClockIn(timeZone, instant)) - instant;

/**
 * The instant at which a wall clock reads this, in `timeZone`.
 *
 * The inverse of {@link wallClockIn}, and the only part of this file that is not a one-liner. The
 * offset to subtract is a function of the instant being solved for, so the first guess uses the
 * offset in force at the naive reading and then checks itself:
 *
 * - Normally the second reading agrees and the first guess was right.
 * - In the autumn hour that happens twice, both readings are valid instants; the earlier one is
 *   taken, matching every other implementation and reading as the earlier clock.
 * - In the spring hour that never happens, neither agrees, and the two offsets bracket the gap.
 *   The smaller offset yields the later instant, which is the first moment that does exist — so a
 *   day whose midnight was skipped starts at 1 AM rather than at 11 PM the evening before.
 */
const instantOf = (timeZone: string, wall: Wall): number => {
  const naive = asUtc(wall);
  const guess = naive - offsetAt(timeZone, naive);
  const settled = naive - offsetAt(timeZone, guess);

  if (guess === settled) return guess;

  const alternative = naive - offsetAt(timeZone, settled);

  return settled === alternative ? settled : Math.max(guess, settled);
};

/** Which day of the week an instant falls on here, as `Date` numbers it: Sunday is 0. */
const weekdayIn = (timeZone: string, instant: number): number =>
  new Date(asUtc(wallClockIn(timeZone, instant))).getUTCDay();

/** `Date`'s own numbering, so the arithmetic below needs no translation. */
const WEEK_STARTS_ON: Readonly<Record<FirstDayOfWeek, number>> = {
  SUNDAY: 0,
  MONDAY: 1,
  SATURDAY: 6,
};

const MIDNIGHT = [0, 0, 0, 0] as const;

/**
 * Binds every operation to one zone and one week start.
 *
 * Pure, and cheap enough to call per render, but the identity matters: the React layer memoizes on
 * the preference signature so the bundle is safe in a dependency array, exactly as the format
 * bundle is.
 *
 * @param preferences - Supplies the time zone and the first day of the week.
 */
export const buildCalendarBundle = (
  preferences: PreferenceValues
): CalendarBundle => {
  const zone = preferences.timeZone;

  const wall = (value: Date): Wall => wallClockIn(zone, value.getTime());

  const at = (fields: Wall): Date => new Date(instantOf(zone, fields));

  const dayOf = (value: Date): string => {
    const [year, monthIndex, day] = wall(value);

    return [
      String(year).padStart(4, '0'),
      String(monthIndex + 1).padStart(2, '0'),
      String(day).padStart(2, '0'),
    ].join('-');
  };

  const startOfDay = (value: Date): Date => {
    const [year, monthIndex, day] = wall(value);

    return at([year, monthIndex, day, ...MIDNIGHT]);
  };

  /** The last instant before a boundary, which is how every `endOf` here is defined. */
  const justBefore = (boundary: Date): Date => new Date(boundary.getTime() - 1);

  const plainDay = (day: string): Date => {
    const [year, monthIndex, dayOfMonth] = fieldsOf(day);

    return new Date(asUtc([year, monthIndex, dayOfMonth, ...MIDNIGHT]));
  };

  const startOfMonth = (value: Date): Date => {
    const [year, monthIndex] = wall(value);

    return at([year, monthIndex, 1, ...MIDNIGHT]);
  };

  const shift = (value: Date, by: CalendarShift): Date => {
    const [year, monthIndex, day, hour, minute, second, millisecond] =
      wall(value);

    const months = monthIndex + (by.years ?? 0) * 12 + (by.months ?? 0);

    // A month before 31 March is 31 February, which `Date` rolls into March. Clamping to the
    // month's own length is what makes it 28 February instead.
    const shifted = new Date(0);
    shifted.setUTCFullYear(year, months, 1);

    // Day zero of the next month is the last of this one. Set rather than constructed for the same
    // reason as everywhere else here: `Date.UTC` would read a two-digit year as 19xx and measure a
    // different February.
    const monthEnd = new Date(0);
    monthEnd.setUTCFullYear(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth() + 1,
      0
    );
    const lastDay = monthEnd.getUTCDate();

    return at([
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      Math.min(day, lastDay) + (by.weeks ?? 0) * 7 + (by.days ?? 0),
      hour,
      minute,
      second,
      millisecond,
    ]);
  };

  return {
    zone,
    firstDayOfWeek: preferences.formats.firstDayOfWeek,
    dayOf,
    startOfDay,
    endOfDay: (value) => justBefore(startOfDay(shift(value, { days: 1 }))),
    startOfMonth,
    endOfMonth: (value) =>
      justBefore(startOfMonth(shift(value, { months: 1 }))),
    startOfWeek: (value) => {
      const start = startOfDay(value);
      const back =
        (weekdayIn(zone, start.getTime()) -
          WEEK_STARTS_ON[preferences.formats.firstDayOfWeek] +
          7) %
        7;

      return startOfDay(shift(start, { days: -back }));
    },
    shift,
    isSameDay: (a, b) => dayOf(a) === dayOf(b),
    dayBounds: (day) => {
      const [year, monthIndex, dayOfMonth] = fieldsOf(day);
      const from = at([year, monthIndex, dayOfMonth, ...MIDNIGHT]);

      return { from, to: justBefore(startOfDay(shift(from, { days: 1 }))) };
    },
    plainDay,
    plainDayFrom: (value) => {
      // Parsed before being read, so the shapes `Date` guesses at are refused here too rather than
      // resolved against a field order the payload never stated.
      const parsed = parseValue(value);

      if (!parsed) return undefined;

      if (typeof value === 'string') {
        const named = DAY_PREFIX.exec(value.trim())?.[0];

        // The day the payload names, which an offset can carry the instant off but not the text.
        // `Date` answers `2026-02-31` with 3 March rather than refusing, so a day that cannot
        // exist would otherwise come back as a real one nobody named.
        if (named) return isRealDay(named) ? plainDay(named) : undefined;
      }

      // A `Date` names no day, so the UTC one is all it carries.
      const { date } = parsed;

      return new Date(
        asUtc([
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          ...MIDNIGHT,
        ])
      );
    },
  };
};
